import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, CreditCard, Save, Share } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import TreatmentPlanBuilderV2 from '@/components/v2/planner/TreatmentPlanBuilderV2';
import CreditBalanceDisplay from '@/components/v2/planner/CreditBalanceDisplay';
import MembershipCTA from '@/components/v2/planner/MembershipCTA';
import PDFExportServiceV2 from '@/components/v2/planner/PDFExportServiceV2';
import { toast } from 'sonner';

const TreatmentPlannerV2 = () => {
  const [currentPlan, setCurrentPlan] = useState(null);
  const [isMember, setIsMember] = useState(false);

  // Fetch user's current credit balance
  const { data: creditBalance = 0 } = useQuery({
    queryKey: ['user-credit-balance'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;
      
      const { data, error } = await supabase.rpc('get_user_credit_balance', {
        user_uuid: user.id
      });
      
      if (error) throw error;
      return data || 0;
    }
  });

  // Fetch user's membership status
  const { data: membershipStatus } = useQuery({
    queryKey: ['user-membership'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('membership_status, membership_tier')
        .eq('user_id', user.id)
        .single();
      
      if (error) throw error;
      return data;
    }
  });

  useEffect(() => {
    if (membershipStatus) {
      setIsMember(membershipStatus.membership_status === 'active');
    }
  }, [membershipStatus]);

  const handleSaveDraft = async () => {
    if (!currentPlan) {
      toast.error('No plan to save');
      return;
    }
    
    try {
      // Save plan logic here
      toast.success('Plan saved as draft');
    } catch (error) {
      toast.error('Failed to save plan');
    }
  };

  const handleSharePlan = async () => {
    if (!currentPlan) {
      toast.error('No plan to share');
      return;
    }
    
    try {
      // Share plan logic here
      toast.success('Plan shared successfully');
    } catch (error) {
      toast.error('Failed to share plan');
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header Section */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center space-x-2">
          <Sparkles className="h-8 w-8 text-juv-terracotta" />
          <h1 className="text-4xl font-bold text-juv-deep-chestnut">
            Treatment Planner 2.0
          </h1>
        </div>
        <p className="text-lg text-juv-deep-chestnut/70">
          Design your personalized aesthetic journey with real-time credit tracking
        </p>
      </div>

      {/* Credit Balance & Membership Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <CreditBalanceDisplay 
          balance={creditBalance} 
          isMember={isMember}
        />
        
        {!isMember && <MembershipCTA />}
      </div>

      {/* Treatment Plan Builder */}
      <TreatmentPlanBuilderV2
        onPlanChange={setCurrentPlan}
        creditBalance={creditBalance}
        isMember={isMember}
      />

      {/* Action Buttons */}
      {currentPlan && (
        <Card className="border-juv-rose-clay/20 bg-white">
          <CardContent className="p-6">
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                onClick={handleSaveDraft}
                variant="outline"
                className="text-juv-deep-chestnut border-juv-rose-clay/30 hover:bg-juv-soft-blush/30"
              >
                <Save className="h-4 w-4 mr-2" />
                Save as Draft
              </Button>
              
              <Button
                onClick={handleSharePlan}
                variant="outline"
                className="text-juv-deep-chestnut border-juv-rose-clay/30 hover:bg-juv-soft-blush/30"
              >
                <Share className="h-4 w-4 mr-2" />
                Share with Patient
              </Button>
              
              <PDFExportServiceV2 
                treatmentPlan={currentPlan}
                isMember={isMember}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TreatmentPlannerV2;
